import type {
  AutocompleteResponse,
  BrowseResultData,
  Facet,
  Item,
  SortOption,
} from "@constructor-io/constructorio-client-javascript";
import type {
  ConstructorProductData,
  ConstructorSectionKeys,
  ConstructorSuggestionData,
} from "./types";
import { isExpectedData } from "src/utils/type-guards";
import {
  BROWSE_BRANDS_FACET_TO_CONSTRUCTOR,
  BROWSE_CATEGORIES_FACET_TO_CONSTRUCTOR,
  CONSTRUCTOR_CAP_RESULTS,
  CONSTRUCTOR_DEPARTMENTS,
  CONSTRUCTOR_FACETS,
  CONSTRUCTOR_LABEL_TYPE,
  CONSTRUCTOR_PROMOTION_TYPE,
  CONSTRUCTOR_TO_LISTING_FACET,
  CONSTRUCTOR_TO_LISTING_SORT_KEY,
  LISTING_FACET_TO_CONSTRUCTOR,
  PRODUCT_SUGGESTION_DATA_FIELDS,
  SUGGESTION_DATA_FIELDS,
  CATEGORY_FIELDS,
  BROWSE_PRODUCTS_FACET_TO_CONSTRUCTOR,
  CONSTRUCTOR_COLLECTIONS_DATA_FIELDS,
  CONSTRUCTOR_BRAND_DATA_FIELDS,
  CONSTRUCTOR_CATEGORY_DATA_FIELDS,
  SUGGESTION_TYPE_ORDER,
  DEFAULT_MAX_SUGGESTIONS,
} from "./constants";
import type {
  ProductSuggestion,
  Suggestion,
} from "src/types/adapters/query.type";
import type {
  ListingFacetBrandData,
  ListingFacetCategoryData,
  ListingFacetCollectionData,
  ListingFacetKeys,
  ListingFacets,
  ListingParams,
  ListingSortDirection,
  ListingSortOptions,
  SelectedFacets,
} from "src/types/adapters/listing.type";
import {
  BrandGateLevelInCustomerPlatform,
  type BrandSaleType,
} from "src/types/graphql.d";
import type { CatalogProductWithBrandSlug } from "src/types/product-listing";
import type {
  AllBrandData,
  BrandData,
  BrowseParams,
  SearchCategoryData,
  CategoryData,
} from "src/types/adapters/browse.type";
import { CONSTRUCTOR_SECTIONS } from "src/constants/constructor";
import type { BreadcrumbItem } from "src/organisms/Breadcrumb";
import { localeDomainMapV2 } from "src/utils/domains";

const APP_LOCALES = new Set(Object.keys(localeDomainMapV2));

export function mapProductSuggestions(
  item: Item,
  idx: number
): ProductSuggestion[] {
  const data = item.data || {};

  if (
    !isExpectedData<ConstructorProductData>(
      data,
      PRODUCT_SUGGESTION_DATA_FIELDS
    )
  ) {
    return [];
  }

  return [
    {
      id: data.id,
      name: item.value,
      thumbnail: data.image,
      position: idx,
      genders: data.genders,
      labels: data.labels || [],
      brandName: data.brand_name,
      brandId: data.brand_id,
      regularPrice: data.regular_price || 0,
      price: data.price || 0,
      sizes: (data.container_sizes || []).map((size) => size.name),
      slug: data.slug,
      gateLevel: data.gate?.level,
      currency: data.currency,
      type: "product" as const satisfies ProductSuggestion["type"],
    },
  ];
}

export function hasDuplicatedSuggestion(data: Item[]) {
  const categoryCounts = data.reduce<Record<string, number>>((acc, item) => {
    const itemData = item.data || {};

    if (
      isExpectedData<ConstructorSuggestionData>(
        itemData,
        SUGGESTION_DATA_FIELDS
      ) &&
      itemData.type === "category"
    ) {
      acc[itemData.query] = (acc[itemData.query] ?? 0) + 1;
    }

    return acc;
  }, {});

  return data.map((item) => {
    const itemData = item.data || {};
    const query = isExpectedData<ConstructorSuggestionData>(
      itemData,
      SUGGESTION_DATA_FIELDS
    )
      ? itemData.query
      : "";

    return {
      ...item,
      hasDuplicate: (categoryCounts[query] ?? 0) > 1,
    };
  });
}

export function mapToCatalogProduct(
  data: ConstructorProductData,
  name: string
): CatalogProductWithBrandSlug {
  // Constructor docs can have any "required" field missing in practice, and
  // Next.js's getStaticProps cannot serialize `undefined`. Required scalars
  // fall back to safe defaults; optional fields fall back to `null`.
  const brandInfo = data.brand_info || null;
  const containerSizes = data.container_sizes || [];
  const categories = data.categories || [];
  const goodOnYou = brandInfo?.good_on_you || [];

  return {
    __typename: "CatalogProduct",
    id: data.id || "",
    name,
    slug: data.slug || "",
    price: data.price ?? 0,
    regular_price: data.regular_price ?? 0,
    thumbnail: data.image || "",
    images: [{ __typename: "BrandImageForGate", url: data.image || null }],
    available_sizes: containerSizes.map((size) => size?.id || null),
    available_sizes_label: data.available_sizes_label || null,
    currency: data.currency,
    gate: data.gate ? { level: data.gate.level || null } : null,
    labels: data.labels || null,
    product_shop_type: data.genders || null,
    translated_category_ids: categories.map((c) =>
      c?.id ? Number(c.id) : null
    ),
    translated_category_names: categories.flatMap((c) =>
      c?.name ? [c.name] : []
    ),
    brand: {
      __typename: "CatalogBrand",
      id: data.brand_id || "",
      name: data.brand_name || "",
      slug: brandInfo?.slug || "",
      brand_sale_type: (brandInfo?.brand_sale_type as BrandSaleType) || null,
      designer_brand: brandInfo?.designer_brand || false,
      gate: { level: brandInfo?.gate?.level || null },
      good_on_you_pillars: goodOnYou,
      is_good_on_you: goodOnYou.length > 0,
    },
  };
}

export function getFacetConfig(params: ListingParams): {
  facetName: string;
  facetValue: string;
  section: string;
} {
  switch (params.productListType) {
    case "CATEGORY_PLP":
    case "SHOP_TYPE_PLP":
      if (params.categoryId) {
        return {
          facetName: CONSTRUCTOR_FACETS.GROUP_ID,
          facetValue: params.categoryId,
          section: CONSTRUCTOR_SECTIONS.PRODUCTS,
        };
      }
      break;

    case "BRAND_PLP":
      return {
        facetName: LISTING_FACET_TO_CONSTRUCTOR.brands,
        facetValue: params.slug,
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    case "COLLECTIONS_PLP":
      // Confirmed against a real Constructor account: collection_id resolves
      // against the Products section, not a dedicated "Collections" section
      // (that 404s - it isn't where collection membership is indexed).
      return {
        facetName: CONSTRUCTOR_FACETS.COLLECTION_ID,
        facetValue: params.slug,
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    case "HOT_DEALS_PLP":
      return {
        facetName: CONSTRUCTOR_FACETS.PROMOTION_FILTER,
        facetValue: CONSTRUCTOR_PROMOTION_TYPE.HOT_DEALS,
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    case "NEW_IN_PLP":
      return {
        facetName: CONSTRUCTOR_FACETS.LABEL_TYPE,
        facetValue: CONSTRUCTOR_LABEL_TYPE.NEW,
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    case "DESIGNER_PLP":
      return {
        facetName: CONSTRUCTOR_FACETS.DEPARTMENTS,
        facetValue: CONSTRUCTOR_DEPARTMENTS.DESIGNER,
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    case "SALE_PLP":
      return {
        facetName: CONSTRUCTOR_FACETS.SALE,
        facetValue: "True",
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
      };

    default:
      params.productListType satisfies "SEARCH_PLP";
  }

  return {
    facetName: CONSTRUCTOR_FACETS.IS_DELETED,
    facetValue: "False",
    section: CONSTRUCTOR_SECTIONS.PRODUCTS,
  };
}

export function toListingFacetKey(
  constructorKey: string
): ListingFacetKeys | undefined {
  return CONSTRUCTOR_TO_LISTING_FACET[
    constructorKey as keyof typeof CONSTRUCTOR_TO_LISTING_FACET
  ];
}

export function toConstructorFacetKey(
  constructorKey: ListingFacetKeys
): (typeof LISTING_FACET_TO_CONSTRUCTOR)[ListingFacetKeys] {
  return LISTING_FACET_TO_CONSTRUCTOR[constructorKey];
}

export function toListingFacets(facets: Facet[]): ListingFacets {
  const map: ListingFacets = new Map();

  for (const facet of facets) {
    if (facet.hidden) {
      continue;
    }

    const key = toListingFacetKey(facet.name);
    if (!key) {
      continue;
    }

    if (facet.type === "range") {
      const min = Math.floor(typeof facet.min === "number" ? facet.min : 0);
      const max = Math.ceil(typeof facet.max === "number" ? facet.max : 0);

      map.set(key, {
        type: "range",
        key,
        label: facet.display_name,
        data: { min, max },
      });

      continue;
    }

    if (key === "colors") {
      map.set(key, {
        type: "color",
        key,
        label: facet.display_name,
        data: facet.options.map((option) => ({
          id: typeof option.data?.id === "string" ? option.data.id : "",
          key: facet.name,
          value: option.value,
          label: option.display_name,
          count: option.count,
          selected: option.status === "selected",
          hex: typeof option.data?.hex === "string" ? option.data.hex : "",
          icon: typeof option.data?.icon === "string" ? option.data.icon : "",
        })),
      });

      continue;
    }

    if (key === "category") {
      map.set(key, {
        type: "category",
        key,
        label: facet.display_name,
        data: facet.options.map((option) => ({
          id: typeof option.data?.id === "string" ? option.data.id : "",
          key: facet.name,
          value: option.value,
          label: option.display_name,
          count: option.count,
          selected: option.status === "selected",
          categoryPageId: Array.isArray(option.data?.category_page_id)
            ? option.data.category_page_id.filter(
                (v): v is string => typeof v === "string"
              )
            : [],
          genders: Array.isArray(option.data?.genders)
            ? option.data.genders.filter(
                (v): v is string => typeof v === "string"
              )
            : [],
          image:
            typeof option.data?.image === "string" ? option.data.image : "",
          parent:
            typeof option.data?.parent === "string" ? option.data.parent : "",
          parentPathString:
            typeof option.data?.parent_path_string === "string"
              ? option.data.parent_path_string
              : "",
          slug: typeof option.data?.slug === "string" ? option.data.slug : "",
        })),
      });

      continue;
    }

    if (key === "size") {
      map.set(key, {
        type: "size",
        key,
        label: facet.display_name,
        data: facet.options.map((option) => ({
          key: facet.name,
          value: option.value,
          label: option.display_name,
          count: option.count,
          selected: option.status === "selected",
          id: typeof option.data?.id === "string" ? option.data.id : "",
          name: typeof option.data?.name === "string" ? option.data.name : "",
          schema:
            typeof option.data?.schema === "string" ? option.data.schema : "",
          unit: typeof option.data?.unit === "string" ? option.data.unit : "",
        })),
      });

      continue;
    }

    if (key === "brands") {
      map.set(key, {
        type: "brand",
        key,
        label: facet.display_name,
        data: facet.options.map((option) => ({
          id: typeof option.data?.id === "string" ? option.data.id : "",
          key: facet.name,
          value: option.value,
          label: option.display_name,
          count: option.count,
          selected: option.status === "selected",
          departments: Array.isArray(option.data?.departments)
            ? option.data.departments.filter(
                (v): v is string => typeof v === "string"
              )
            : [],
          designerBrand: option.data?.designer_brand === true,
          goodOnYouInfo:
            typeof option.data?.good_on_you_info === "string"
              ? option.data.good_on_you_info
              : null,
          logo: typeof option.data?.logo === "string" ? option.data.logo : "",
          slug: typeof option.data?.slug === "string" ? option.data.slug : "",
        })),
      });

      continue;
    }

    const data = facet.options.map((option) => ({
      key: facet.name,
      value: option.value,
      label: option.display_name,
      count: option.count,
      selected: option.status === "selected",
    }));

    map.set(
      key,
      facet.type === "multiple" && key !== "discount"
        ? {
            type: "multiple",
            key,
            label: facet.display_name,
            data,
          }
        : {
            type: "single",
            key,
            label: facet.display_name,
            data,
          }
    );
  }

  return map;
}

export function findLeafSiblingsGroupId(
  facets: ListingFacets,
  categoryId?: string
): string | undefined {
  if (!categoryId) {
    return undefined;
  }

  const facet = facets.get("category");

  if (facet?.type !== "category") {
    return undefined;
  }

  const focus = facet.data.find((item) => item.id === categoryId);

  if (!focus?.parent) {
    return undefined;
  }

  const hasChildren = facet.data.some((item) => item.parent === categoryId);

  return hasChildren ? undefined : focus.parent;
}

export function mergeSiblingCategories(
  facets: ListingFacets,
  siblingFacets: ListingFacets,
  parentId: string
): ListingFacets {
  const facet = facets.get("category");
  const siblingFacet = siblingFacets.get("category");

  if (facet?.type !== "category" || siblingFacet?.type !== "category") {
    return facets;
  }

  const known = new Set(facet.data.map((item) => item.id));
  const siblings = siblingFacet.data.filter(
    (item) => item.parent === parentId && !known.has(item.id)
  );

  if (!siblings.length) {
    return facets;
  }

  return new Map(facets).set("category", {
    ...facet,
    data: [...facet.data, ...siblings],
  });
}

export function toConstructorFacets(
  filters: Partial<Record<ListingFacetKeys, string[]>>
) {
  const facets = Object.entries(filters).reduce(
    (acc, [facetKey, facetValue]) => {
      const key = toConstructorFacetKey(facetKey as ListingFacetKeys);

      acc[key] = facetValue;

      return acc;
    },
    {} as Partial<
      Record<(typeof LISTING_FACET_TO_CONSTRUCTOR)[ListingFacetKeys], string[]>
    >
  );

  return facets;
}

function toListingSortDirection(sortOrder: string): ListingSortDirection {
  return sortOrder === "ascending" ? "ascending" : "descending";
}

export function mapSortOptions(
  sortOptions: SortOption[] | undefined
): ListingSortOptions {
  const result: ListingSortOptions = {};

  if (!sortOptions) {
    return result;
  }

  for (const option of sortOptions) {
    if (option.hidden) {
      continue;
    }

    const direction = toListingSortDirection(option.sort_order);
    const candidates = CONSTRUCTOR_TO_LISTING_SORT_KEY[option.sort_by];

    if (!candidates) {
      continue;
    }

    const match = candidates.find(
      (candidate) => candidate.direction === direction
    );

    if (!match) {
      continue;
    }

    result[match.key] = {
      direction,
      value: option.sort_by,
    };
  }

  return result;
}

export function buildSelectedFacetFilters(
  selectedFacets: SelectedFacets | undefined
): Record<string, string[]> {
  if (!selectedFacets) {
    return {};
  }

  const result: Record<string, string[]> = {};

  for (const [key, values] of Object.entries(selectedFacets) as [
    ListingFacetKeys,
    string[] | undefined
  ][]) {
    if (!values || values.length === 0) {
      continue;
    }

    const constructorKey = LISTING_FACET_TO_CONSTRUCTOR[key];
    if (!constructorKey) {
      continue;
    }

    result[constructorKey] = values;
  }

  return result;
}

export function getFacetName(params: BrowseParams): string | undefined {
  switch (params.entity) {
    case "products":
      return BROWSE_PRODUCTS_FACET_TO_CONSTRUCTOR[params.facetName];
    case "brands":
      return BROWSE_BRANDS_FACET_TO_CONSTRUCTOR[params.facetName];
    case "facets":
      return params.facetName
        ? BROWSE_PRODUCTS_FACET_TO_CONSTRUCTOR[params.facetName]
        : CONSTRUCTOR_FACETS.IS_DELETED;
    case "all-brands":
      return BROWSE_BRANDS_FACET_TO_CONSTRUCTOR[params.facetName];
    case "categories":
      return BROWSE_CATEGORIES_FACET_TO_CONSTRUCTOR[params.facetName];
    default:
      return undefined;
  }
}

export function getTotalResults(response: {
  result_sources?: { token_match?: { count: number } };
  total_num_results?: number;
}): number {
  const tokenMatchCount = response.result_sources?.token_match?.count || 0;

  return (
    (tokenMatchCount > CONSTRUCTOR_CAP_RESULTS
      ? tokenMatchCount
      : response.total_num_results) || 0
  );
}

export function mapToBrands(rawResults: BrowseResultData[]): BrandData[] {
  return rawResults.map((item) => {
    const data = item.data as Omit<BrandData, "name" | "resultId">;
    const emptyByShopType = { women: [], men: [], kids: [] };
    const nullByShopType = { women: null, men: null, kids: null };

    return {
      id: data.id || "",
      name: item.value,
      logo: data.logo || "",
      slug: data.slug || "",
      gate: data.gate || {},
      images: data.images || emptyByShopType,
      labels: data.labels || emptyByShopType,
      woo_id: data.woo_id || "",
      is_gold: data.is_gold || false,
      genders: data.genders || [],
      is_deleted: data.is_deleted || false,
      description: data.description || "",
      departments: data.departments || [],
      cover_images: data.cover_images || nullByShopType,
      is_good_on_you: data.is_good_on_you || false,
      designer_brand: data.designer_brand || false,
      discount_labels: data.discount_labels || nullByShopType,
      brand_sale_type: data.brand_sale_type,
      good_on_you_info: data.good_on_you_info || null,
      about_brand_text: data.about_brand_text || null,
      category_page_id: data.category_page_id || [],
      about_popup_image: data.about_popup_image || "",
      store_publish_date: data.store_publish_date || 0,
      overlay_logo_images: data.overlay_logo_images || [],
      good_on_you_pillars: data.good_on_you_pillars || [],
    };
  });
}

export function mapToAllBrands(facets: Facet[]): AllBrandData[] {
  const brandsFacet = facets.find(
    (facet) => facet.name === LISTING_FACET_TO_CONSTRUCTOR.brands
  );

  if (!brandsFacet?.options) {
    return [];
  }

  return brandsFacet.options.map((option: Facet) => ({
    id: typeof option.data.id === "string" ? option.data.id : "",
    name:
      typeof option.data.name === "string" ? option.data.name : option.value,
    slug: typeof option.data.slug === "string" ? option.data.slug : "",
    value: typeof option.value === "string" ? option.value : "",
  }));
}

export function mapToCategory(
  rawResponse: BrowseResultData
): CategoryData | null {
  const rawData = rawResponse.data;

  if (
    !rawData ||
    !isExpectedData<SearchCategoryData>(rawData, CATEGORY_FIELDS)
  ) {
    return null;
  }

  return {
    id: rawData.id || "",
    name: rawResponse.value,
    slug: rawData.slug,
    wooId: rawData.woo_id,
    parentId: rawData.parent,
    isDeleted: rawData.is_deleted,
    categoryPageId: rawData.category_page_id || [],
    parentPathString: rawData.parent_path_string,
    excludedFromFilters: rawData.excluded_from_filters,
    localisedCategorySlugs: rawData.localised_category_slugs
      ? Object.fromEntries(
          rawData.localised_category_slugs.flatMap(({ locale, slug }) => {
            // Constructor uses BCP 47 with region (e.g. "de-DE", "fr-FR").
            // App locale keys use a simplified format ("de", "fr", "en-gb").
            // Normalize: lowercase first, then fall back to language-only prefix.
            const lower = locale.toLowerCase();
            const lang = lower.split("-")[0];
            const appLocale = APP_LOCALES.has(lower)
              ? lower
              : APP_LOCALES.has(lang)
              ? lang
              : null;
            return appLocale ? [[appLocale, slug]] : [];
          })
        )
      : undefined,
  };
}

type SuggestionItemData = {
  id: string;
  slug: string;
  url: string;
  gate?: ConstructorSuggestionData["gate"];
  parent_path_string?: string;
};

type SectionConfig = {
  fields: readonly string[];
  facetKeys: Set<string>;
  facetKeyOf: (data: SuggestionItemData, value: string) => string;
  suggestionType: Suggestion["type"];
};

function highlightMatchedTerms(
  text: string,
  matchedTerms: string[] | undefined
): string {
  const terms = matchedTerms
    ?.filter(Boolean)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  if (!terms?.length) {
    return text;
  }

  return text.replace(new RegExp(`(${terms.join("|")})`, "gi"), "<em>$1</em>");
}

export function buildSuggestions(
  sections: AutocompleteResponse["sections"],
  facets: Facet[],
  maxSuggestions: number = DEFAULT_MAX_SUGGESTIONS
): Suggestion[] {
  const mappedFacets = toListingFacets(facets);

  const brandSlugs = new Set(
    ((mappedFacets.get("brands")?.data ?? []) as ListingFacetBrandData[]).map(
      (brand) => brand.slug || brand.value
    )
  );
  const categorySlugs = new Set(
    (
      (mappedFacets.get("category")?.data ?? []) as ListingFacetCategoryData[]
    ).map((category) => category.slug)
  );
  const collectionIds = new Set(
    (
      (mappedFacets.get("collections")?.data ??
        []) as ListingFacetCollectionData[]
    ).map((collection) => collection.value)
  );

  const sectionConfig: Partial<Record<ConstructorSectionKeys, SectionConfig>> =
    {
      [CONSTRUCTOR_SECTIONS.BRANDS]: {
        fields: CONSTRUCTOR_BRAND_DATA_FIELDS,
        facetKeys: brandSlugs,
        facetKeyOf: (data, value) => data.slug || value,
        suggestionType: "brand",
      },
      [CONSTRUCTOR_SECTIONS.CATEGORIES]: {
        fields: CONSTRUCTOR_CATEGORY_DATA_FIELDS,
        facetKeys: categorySlugs,
        facetKeyOf: (data) => data.slug,
        suggestionType: "category",
      },
      [CONSTRUCTOR_SECTIONS.COLLECTIONS]: {
        fields: CONSTRUCTOR_COLLECTIONS_DATA_FIELDS,
        facetKeys: collectionIds,
        facetKeyOf: (data) => data.id,
        suggestionType: "collection",
      },
    };

  const buckets: Record<Suggestion["type"], Suggestion[]> = {
    brand: [],
    category: [],
    collection: [],
  };

  for (const [sectionKey, response] of Object.entries(sections)) {
    const config = sectionConfig[sectionKey as ConstructorSectionKeys];

    if (!config) {
      continue;
    }

    for (const item of response) {
      if (
        !isExpectedData<SuggestionItemData>(item.data, config.fields) ||
        !config.facetKeys.has(config.facetKeyOf(item.data, item.value))
      ) {
        continue;
      }

      const gateLevel = item.data.gate?.level;

      buckets[config.suggestionType].push({
        id: item.data.id,
        slug: item.data.slug,
        title: highlightMatchedTerms(item.value, item.matched_terms),
        url: item.data.url,
        gateLevel,
        isGated:
          gateLevel !== undefined &&
          gateLevel !== BrandGateLevelInCustomerPlatform.NonGated,
        matchedTerm: item.matched_terms,
        parentPathString: item.data.parent_path_string,
        type: config.suggestionType,
      });
    }
  }

  const taken: Record<Suggestion["type"], Suggestion[]> = {
    brand: [],
    category: [],
    collection: [],
  };
  const cursors: Record<Suggestion["type"], number> = {
    brand: 0,
    category: 0,
    collection: 0,
  };

  let total = 0;
  let pickedThisRound = true;
  while (pickedThisRound && total < maxSuggestions) {
    pickedThisRound = false;

    for (const type of SUGGESTION_TYPE_ORDER) {
      if (total >= maxSuggestions) {
        break;
      }

      const bucket = buckets[type];
      const idx = cursors[type];

      if (idx < bucket.length) {
        taken[type].push(bucket[idx]);
        cursors[type] = idx + 1;
        total++;
        pickedThisRound = true;
      }
    }
  }

  const result = SUGGESTION_TYPE_ORDER.flatMap((type) => taken[type]);

  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const suggestion of result) {
    const key = suggestion.title.toLowerCase();

    if (seen.has(key)) {
      duplicates.add(key);

      continue;
    }

    seen.add(key);
  }

  return result.map((suggestion) =>
    duplicates.has(suggestion.title.toLowerCase())
      ? {
          ...suggestion,
          breadcrumb:
            suggestion.parentPathString
              ?.split(" > ")
              .map<BreadcrumbItem>((str) => ({ title: str })) || [],
        }
      : suggestion
  );
}
